// The Inbox page (`/c/{slug}/inbox`; design 2026-10-30 §3 "Inbox: its own full destination";
// INBOX-PAGE-MAIN: the main Inbox — the rail's Inbox, Home's rows, a push notification and the
// bell peek's "Open Inbox" land here). `InboxList` as a split page: tabs, Filter, Mark all read
// and the crew's suggestions over the list on the left, the picked item on the right with its
// actions (decide inline, reply, open its source). j/k/e/s/u/⏎ work from the moment it opens.
// RECALL-INBOX-LOOP: with the memory add-on on, a second tab, "Memory", is the one Memory lane.
import { CountPill, ViewTabs } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { createRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CommitmentsCard } from "../features/commitments/commitments-card";
import { CrewSuggestions } from "../features/crew/crew-suggestions";
import { CatchUpCard } from "../features/memory/catch-up-card";
import { MemoryLane } from "../features/memory/memory-lane";
import { memoryLaneQuery } from "../features/memory/memory-queries";
import { PeopleCards } from "../features/memory/people-cards";
import { useCompany, useFeature } from "../features/use-feature";
import { InboxList, useInboxUnread } from "../features/work/inbox-list";
import { validateFocusSearch } from "../focus/focus";
import { companyRoute } from "./c.$slug";

export const inboxRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "inbox",
  // `tab=memory` opens the Memory lane; `card` (`{kind}:{id}`) is the card to bring into view
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(search.tab === "memory" ? { tab: "memory" as const } : {}),
    ...(typeof search.card === "string" && /^[a-z_]+:[0-9a-f-]{36}$/.test(search.card)
      ? { card: search.card }
      : {}),
    // CLOSED-LOOPS: a commitment's inbox item opens it in the Commitments card
    ...(typeof search.commitment === "string" && /^[0-9a-f-]{36}$/.test(search.commitment)
      ? { commitment: search.commitment }
      : {}),
  }),
  component: function InboxPage() {
    const { api } = inboxRoute.useRouteContext();
    const company = useCompany();
    const memoryOn = useFeature("memory", company.id).enabled;
    const search = inboxRoute.useSearch();
    const navigate = inboxRoute.useNavigate();
    const [tab, setTab] = useState<"inbox" | "memory">(
      search.tab === "memory" ? "memory" : "inbox",
    );
    // a memory row opened from the Inbox list lands on the Memory tab (also when already here)
    useEffect(() => {
      if (search.tab === "memory") setTab("memory");
    }, [search.tab]);
    const unread = useInboxUnread(api, company.id, true);
    const lane = useQuery({ ...memoryLaneQuery(api, company.id), enabled: memoryOn });
    const memoryOpen = lane.data?.open ?? 0;
    return (
      <div data-slot="inbox-page" className="flex h-full min-h-0 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-subtle px-4">
          <h1 className="text-base font-semibold">Inbox</h1>
          {unread ? <CountPill count={unread} tone="accent" /> : null}
        </header>
        {memoryOn ? (
          <div className="shrink-0 border-b border-subtle px-4">
            <ViewTabs
              aria-label="Inbox"
              value={tab}
              onValueChange={(v) => setTab(v as "inbox" | "memory")}
              tabs={[
                { value: "inbox", label: "Inbox" },
                { value: "memory", label: memoryOpen ? `Memory ${memoryOpen}` : "Memory" },
              ]}
            />
          </div>
        ) : null}
        {memoryOn && tab === "memory" ? (
          <MemoryLane api={api} company={company} focus={search.card ?? null} />
        ) : (
          <>
            <CatchUpCard api={api} company={company} />
            <PeopleCards api={api} company={company} />
            <CommitmentsCard
              api={api}
              company={company}
              focus={search.commitment ?? null}
              onFocusClose={() =>
                void navigate({
                  to: ".",
                  search: (prev: Record<string, unknown>) => {
                    const { commitment: _c, ...rest } = prev;
                    return rest;
                  },
                } as never)
              }
            />
            <CrewSuggestions api={api} company={company} />
            <InboxList key={company.id} api={api} company={company} split keysOnWindow />
          </>
        )}
      </div>
    );
  },
});
